/**
 * Error reporting for the display app.
 *
 * A signage screen has nobody looking at the console, so anything that fails
 * silently stays invisible until a customer complains. Two categories are
 * especially easy to miss:
 *
 *   - Media failures. A <video> or <img> that cannot load fires an `error`
 *     EVENT on the element; it never throws, so nothing reaches Sentry unless
 *     we report it explicitly.
 *   - Playback stalls. The decoder can stop producing frames while the element
 *     still claims to be playing. No error of any kind is raised.
 *
 * Everything here is reporting only — it never changes what the screen does.
 */

import { addBreadcrumb, captureException, captureMessage, setTag } from '@sentry/browser';
import { deferFault } from './deferredFaults';

/**
 * Repeated identical failures are common on a screen that loops the same
 * broken asset every few seconds. Report each distinct problem at most once
 * per window so one bad screen cannot flood the project.
 */
const REPORT_THROTTLE_MS = 10 * 60 * 1000;
const lastReportedAt = new Map<string, number>();

/**
 * Offline faults cannot be sent, so they are stored instead.
 *
 * Measured on the panel: killing the network mid-playlist turns the screen
 * black at the next video and Sentry receives nothing at all. The fault and
 * the inability to report it have the same cause, so every report path needs
 * this — not just the one we happened to be looking at.
 *
 * Returns true when the caller should stop, because the fault has been kept
 * for the next start that has connectivity.
 */
function deferWhileOffline(message: string, detail: Record<string, unknown>): boolean {
  if (navigator.onLine) {
    return false;
  }

  deferFault(message, { ...detail, wasOffline: true });
  return true;
}

function shouldReport(key: string): boolean {
  const now = Date.now();
  const previous = lastReportedAt.get(key);

  if (previous !== undefined && now - previous < REPORT_THROTTLE_MS) {
    return false;
  }

  lastReportedAt.set(key, now);
  return true;
}

/**
 * Identify which screen an event came from. Without this a report tells you
 * something broke but not where, which is most of the diagnostic value.
 * Called once the hub confirms who this display is.
 */
export function setDisplayIdentity(identity: {
  displayId?: number | string | null;
  deviceId?: string | null;
  displayName?: string | null;
  venueId?: number | string | null;
}): void {
  try {
    if (identity.displayId !== undefined && identity.displayId !== null) {
      setTag('displayId', String(identity.displayId));
    }
    if (identity.deviceId) {
      setTag('deviceId', identity.deviceId);
    }
    if (identity.displayName) {
      setTag('displayName', identity.displayName);
    }
    if (identity.venueId !== undefined && identity.venueId !== null) {
      setTag('venueId', String(identity.venueId));
    }
  } catch {
    // Reporting must never break playback.
  }
}

/** Low-value on its own, high-value as context attached to a later error. */
export function trackEvent(category: string, message: string, data?: Record<string, unknown>): void {
  try {
    addBreadcrumb({ category, message, level: 'info', data });
  } catch {
    /* ignore */
  }
}

const MEDIA_ERROR_NAMES: Record<number, string> = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
};

/**
 * A media element failed to load or decode. Reported as a message rather than
 * an exception because there is no throw and therefore no useful stack — the
 * source URL and MediaError code are what matter.
 */
export function reportMediaError(
  kind: 'video' | 'image',
  src: string | null | undefined,
  mediaError?: MediaError | null,
): void {
  const code = mediaError?.code;
  const codeName = code ? (MEDIA_ERROR_NAMES[code] ?? `code ${code}`) : 'no MediaError';

  if (!shouldReport(`media:${kind}:${src}:${code ?? 'none'}`)) {
    return;
  }

  const detail = { src, code, codeName, message: mediaError?.message };

  if (deferWhileOffline(`${kind} failed to load: ${codeName}`, detail)) {
    return;
  }

  captureMessage(`${kind} failed to load: ${codeName}`, {
    level: 'error',
    tags: { failureKind: `${kind}-load` },
    extra: detail,
  });
}

/**
 * Playback stopped without any error being raised — the case that made the
 * white screens invisible. `detail` should carry enough state to tell a dead
 * decoder apart from a slow network.
 */
export function reportPlaybackStall(reason: string, detail: Record<string, unknown>): void {
  if (!shouldReport(`stall:${reason}:${detail.src ?? ''}`)) {
    return;
  }

  if (deferWhileOffline(`Playback stalled: ${reason}`, detail)) {
    return;
  }

  captureMessage(`Playback stalled: ${reason}`, {
    level: 'error',
    tags: { failureKind: 'playback-stall' },
    extra: detail,
  });
}

/** An exception that we caught ourselves and handled, so it still gets recorded. */
export function reportHandledError(error: unknown, context: Record<string, unknown>): void {
  try {
    captureException(error, { tags: { failureKind: 'handled' }, extra: context });
  } catch {
    /* ignore */
  }
}
